import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import InvoiceRevampModal from './InvoiceRevampModal';
import { useAuth } from '../../contexts/AuthContext';
import { Settings, ChevronDown, GitBranch } from 'lucide-react';
import VersionsModal from '../modern/VersionsModal';

interface InvoiceData {
  itinerary_id: string;
  lead_guest_name: string;
  travel_date: string;
  agency_name: string;
  total_amount: number;
  discount: number;
  subtotal_amount: number;
  amount_paid: number;
  balance_amount: number;
  invoice_status: string;
  payment_status: string;
  converted_date: string;
  booking_id: number;
  trip_status: string;
  days_overdue?: number;
  is_verified?: boolean;
  quotation_id?: number;
  version_number?: number;
  quotation_number?: string;
  total_paid_all_versions?: number;
  invoice_needs_review?: boolean;
}

interface Analytics {
  total_invoices: number;
  fully_paid: number;
  partially_paid: number;
  unpaid: number;
  total_outstanding: number;
  total_collected: number;
  total_invoice_amount: number;
  collection_rate: number;
  base_currency?: string;
  by_currency?: {
    [currency: string]: {
      invoice: number;
      paid: number;
      balance: number;
    };
  };
}

const InvoiceRevampPage: React.FC = () => {
  const [invoices, setInvoices] = useState<InvoiceData[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<InvoiceData[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [filteredAnalytics, setFilteredAnalytics] = useState<Analytics | null>(null);
  const [activeView, setActiveView] = useState<'all' | 'outstanding'>('all');
  const [showFilters, setShowFilters] = useState(true);
  const [sortField, setSortField] = useState<keyof InvoiceData | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState({
    itineraryId: '',
    leadGuest: '',
    agencyName: '',
    quotationNumber: '',
    paymentStatus: 'Partial,Not Paid',
    invoiceStatus: '',
    tripStatus: '',
    travelDateFrom: '',
    travelDateTo: '',
    verifiedStatus: '',
    itineraryStatus: ''
  });

  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const canManageInvoices = user?.role === 'ADMIN' || user?.role === 'WYNDER_SALES' || user?.role === 'WYNDER_ACCOUNTS';
  const [versionsItineraryId, setVersionsItineraryId] = useState<number | null>(null);

  useEffect(() => {
    fetchInvoices();
  }, [activeView, filters.travelDateFrom, filters.travelDateTo]);

  useEffect(() => {
    applyFilters();
  }, [filters, invoices]);

  useEffect(() => {
    calculateFilteredAnalytics();
  }, [filteredInvoices]);

  const fetchInvoices = async () => {
    try {
      const token = sessionStorage.getItem('jwt_token');
      const endpoint = activeView === 'outstanding' ? '/api/invoice-revamp/outstanding' : '/api/invoice-revamp/';
      
      const params = new URLSearchParams();
      if (filters.travelDateFrom) params.append('travel_date_from', filters.travelDateFrom);
      if (filters.travelDateTo) params.append('travel_date_to', filters.travelDateTo);
      
      const response = await fetch(`${endpoint}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setInvoices(data.data);
        if (data.analytics) {
          setAnalytics(data.analytics);
        }
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('jwt_token');
      const endpoint = activeView === 'outstanding' ? '/api/invoice-revamp/outstanding' : '/api/invoice-revamp/';
      
      const params = new URLSearchParams();
      if (filters.travelDateFrom) params.append('travel_date_from', filters.travelDateFrom);
      if (filters.travelDateTo) params.append('travel_date_to', filters.travelDateTo);
      if (filters.itineraryId) params.append('itinerary_id', filters.itineraryId);
      if (filters.leadGuest) params.append('lead_guest', filters.leadGuest);
      if (filters.agencyName) params.append('agency_name', filters.agencyName);
      if (filters.paymentStatus) params.append('payment_status', filters.paymentStatus);
      if (filters.invoiceStatus) params.append('invoice_status', filters.invoiceStatus);
      if (filters.tripStatus) params.append('trip_status', filters.tripStatus);
      if (filters.itineraryStatus) params.append('itinerary_status', filters.itineraryStatus);
      
      const response = await fetch(`${endpoint}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setInvoices(data.data);
        if (data.analytics) {
          setAnalytics(data.analytics);
        }
      }
    } catch (error) {
      console.error('Error searching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = invoices;

    if (filters.itineraryId) {
      filtered = filtered.filter(invoice => 
        invoice.itinerary_id.toLowerCase().includes(filters.itineraryId.toLowerCase())
      );
    }
    if (filters.leadGuest) {
      filtered = filtered.filter(invoice => 
        invoice.lead_guest_name.toLowerCase().includes(filters.leadGuest.toLowerCase())
      );
    }
    if (filters.agencyName) {
      filtered = filtered.filter(invoice => 
        invoice.agency_name.toLowerCase().includes(filters.agencyName.toLowerCase())
      );
    }
    if (filters.quotationNumber) {
      filtered = filtered.filter(invoice => 
        (invoice.quotation_number || '').toLowerCase().includes(filters.quotationNumber.toLowerCase())
      );
    }
    if (filters.paymentStatus) {
      const statuses = filters.paymentStatus.split(',');
      filtered = filtered.filter(invoice => statuses.includes(invoice.payment_status));
    }
    if (filters.invoiceStatus) {
      filtered = filtered.filter(invoice => invoice.invoice_status === filters.invoiceStatus);
    }
    if (filters.tripStatus) {
      filtered = filtered.filter(invoice => invoice.trip_status === filters.tripStatus);
    }
    if (filters.itineraryStatus) {
      filtered = filtered.filter(invoice => (invoice as any).itinerary_status === filters.itineraryStatus);
    }
    if (filters.verifiedStatus) {
      // Deprecated: is_verified is now on itinerary_payments, not booked_itineraries
      // Keeping filter structure but it won't match any data from this endpoint
      filtered = filtered.filter(invoice => 
        filters.verifiedStatus === 'verified' ? invoice.is_verified : !invoice.is_verified
      );
    }
    if (filters.travelDateFrom) {
      filtered = filtered.filter(invoice => 
        new Date(invoice.travel_date) >= new Date(filters.travelDateFrom)
      );
    }
    if (filters.travelDateTo) {
      filtered = filtered.filter(invoice => 
        new Date(invoice.travel_date) <= new Date(filters.travelDateTo)
      );
    }

    setFilteredInvoices(filtered);
  };

  const calculateFilteredAnalytics = () => {
    // Use backend analytics only - no frontend currency conversion
    // Backend handles all exchange rate calculations securely
    if (analytics) {
      setFilteredAnalytics(analytics);
    } else {
      setFilteredAnalytics(null);
    }
  };

  const handleSort = (field: keyof InvoiceData) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    if (!sortField) return 0;
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    return 0;
  });

  const handleGearClick = (invoice: InvoiceData, e: React.MouseEvent) => {
    e.stopPropagation();
    if (canManageInvoices) {
      setSelectedInvoice(invoice);
      setShowModal(true);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return 'text-green-600 bg-green-100';
      case 'Partial': return 'text-yellow-600 bg-yellow-100';
      case 'Not Paid': return 'text-red-600 bg-red-100';
      case 'Issued': return 'text-blue-600 bg-blue-100';
      case 'Not Issued': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getOverdueColor = (days: number) => {
    if (days > 30) return 'text-red-700 bg-red-100';
    if (days > 7) return 'text-orange-700 bg-orange-100';
    if (days > 0) return 'text-yellow-700 bg-yellow-100';
    return 'text-gray-600 bg-gray-100';
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <div className="w-full px-3 sm:px-6 lg:px-8 py-3 sm:py-6 flex-1 overflow-y-auto">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Invoice Management - Enhanced</h1>
          <p className="text-sm sm:text-base text-gray-600">Advanced customer payment tracking and management</p>
        </div>

        {/* View Toggle */}
        <div className="flex gap-2 mb-4 sm:mb-6">
          <Button
            variant={activeView === 'all' ? 'primary' : 'secondary'}
            onClick={() => setActiveView('all')}
            size="sm"
            className="text-xs sm:text-sm"
          >
            All Invoices
          </Button>
          <Button
            variant={activeView === 'outstanding' ? 'primary' : 'secondary'}
            onClick={() => setActiveView('outstanding')}
            size="sm"
            className="text-xs sm:text-sm"
          >
            Outstanding Payments
          </Button>
        </div>

        {/* Filters + Summary side by side */}
        <div className="flex gap-4 items-start">
          {/* Filters */}
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm flex-1">
          <div className="flex flex-col gap-3 sm:gap-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 border rounded-lg transition-colors text-sm ${
                showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              Filters
            </button>

            {showFilters && (
              <div className="pt-3 sm:pt-4 border-t border-gray-200">
                <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date From</label>
                      <Input
                        placeholder="Travel From"
                        type="date"
                        value={filters.travelDateFrom}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({...filters, travelDateFrom: e.target.value})}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date To</label>
                      <Input
                        placeholder="Travel To"
                        type="date"
                        value={filters.travelDateTo}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({...filters, travelDateTo: e.target.value})}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Agency Name</label>
                      <Input
                        placeholder="Agency Name"
                        value={filters.agencyName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({...filters, agencyName: e.target.value})}
                        className="text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Itinerary ID</label>
                      <Input
                        placeholder="Itinerary ID"
                        value={filters.itineraryId}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({...filters, itineraryId: e.target.value})}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Quotation Number</label>
                      <Input
                        placeholder="e.g. IQ-0001"
                        value={filters.quotationNumber}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({...filters, quotationNumber: e.target.value})}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Lead Guest Name</label>
                      <Input
                        placeholder="Lead Guest"
                        value={filters.leadGuest}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({...filters, leadGuest: e.target.value})}
                        className="text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
                      <div className="flex flex-wrap gap-2 sm:gap-4">
                        {['Completed', 'Partial', 'Not Paid'].map(status => (
                          <label key={status} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={filters.paymentStatus.split(',').includes(status)}
                              onChange={(e) => {
                                const statuses = filters.paymentStatus.split(',').filter(s => s);
                                if (e.target.checked) {
                                  statuses.push(status);
                                } else {
                                  const index = statuses.indexOf(status);
                                  if (index > -1) statuses.splice(index, 1);
                                }
                                setFilters({...filters, paymentStatus: statuses.join(',')});
                              }}
                              className="mr-1 sm:mr-2"
                            />
                            <span className="text-xs sm:text-sm">{status}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Status</label>
                      <Select
                        value={filters.invoiceStatus}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({...filters, invoiceStatus: e.target.value})}
                        className="text-sm"
                      >
                        <option value="">All Invoice Status</option>
                        <option value="Issued">Issued</option>
                        <option value="Not Issued">Not Issued</option>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Trip Status</label>
                      <Select
                        value={filters.tripStatus}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({...filters, tripStatus: e.target.value})}
                        className="text-sm"
                      >
                        <option value="">All Trip Status</option>
                        <option value="Completed">Completed</option>
                        <option value="Not Completed">Not Completed</option>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Itinerary Status</label>
                      <Select
                        value={filters.itineraryStatus}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({...filters, itineraryStatus: e.target.value})}
                        className="text-sm"
                      >
                        <option value="">All</option>
                        <option value="CONVERTED">Converted</option>
                        <option value="PENDING_CONFIRMATION">Pending Confirmation</option>
                        <option value="TENTATIVE">Tentative</option>
                        <option value="CANCELED">Canceled</option>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Verified Status</label>
                      <Select
                        value={filters.verifiedStatus}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({...filters, verifiedStatus: e.target.value})}
                        className="text-sm"
                      >
                        <option value="">All</option>
                        <option value="verified">Verified</option>
                        <option value="unverified">Unverified</option>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button
                      onClick={handleSearch}
                      disabled={loading}
                      className="px-6 py-2 text-sm"
                    >
                      {loading ? 'Searching...' : 'Search'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>

          {/* Currency Summary Panel */}
          {filteredAnalytics?.by_currency && Object.keys(filteredAnalytics.by_currency).length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-4 w-64 flex-shrink-0 self-start sticky top-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Summary</h3>
              <div className="space-y-3">
                {Object.entries(filteredAnalytics.by_currency)
                  .filter(([, v]) => v.invoice > 0)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([currency, vals]) => (
                    <div key={currency} className="border border-gray-100 rounded-lg p-3">
                      <div className="text-xs font-bold text-gray-500 uppercase mb-2">{currency}</div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Invoice</span>
                          <span className="font-medium text-gray-900">{vals.invoice.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Paid</span>
                          <span className="font-medium text-green-700">{vals.paid.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        </div>
                        <div className="flex justify-between border-t border-gray-100 pt-1">
                          <span className="text-gray-500">Balance</span>
                          <span className={`font-semibold ${vals.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {vals.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500 space-y-0.5">
                <div>{filteredAnalytics.total_invoices} itineraries</div>
                <div className="text-green-600">{filteredAnalytics.fully_paid} fully paid</div>
                <div className="text-yellow-600">{filteredAnalytics.partially_paid} partial</div>
                <div className="text-red-500">{filteredAnalytics.unpaid} unpaid</div>
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <Card className="flex-1 min-h-0">
          <CardContent className="p-0 h-full">
            {/* Mobile Card View (hidden on desktop) */}
            <div className="block sm:hidden h-full">
              <div className="p-2 space-y-2 h-full overflow-y-auto overflow-x-hidden">
                {filteredInvoices.map((invoice) => (
                  <div key={invoice.booking_id} className={`bg-white border border-gray-200 rounded-lg p-3 shadow-sm w-full min-w-0 ${(invoice as any).itinerary_currency === 'USD' ? 'bg-yellow-50' : ''}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center min-w-0 flex-1">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 break-words">
                            {invoice.quotation_number ? (
                              <span
                                className="font-mono font-bold text-blue-700 cursor-pointer hover:text-blue-900"
                                onClick={() => { const id = parseInt(invoice.itinerary_id.replace('WH_', '')); setVersionsItineraryId(id); }}
                              >
                                <GitBranch className="w-3 h-3 inline mr-0.5" />
                                {invoice.quotation_number}/{invoice.version_number}
                              </span>
                            ) : (
                              invoice.itinerary_id
                            )}
                            {invoice.quotation_number && (
                              <span className="ml-1 text-[10px] text-gray-400 font-normal">{invoice.itinerary_id}</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 truncate" title={invoice.lead_guest_name}>
                            {(invoice.lead_guest_name || '').length > 30 ? `${invoice.lead_guest_name.substring(0, 30)}...` : invoice.lead_guest_name}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                        {canManageInvoices && (
                          <button
                            onClick={(e) => handleGearClick(invoice, e)}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Manage Invoice"
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                      <div className="min-w-0">
                        <div className="text-gray-500">Agency:</div>
                        <div className="font-medium truncate" title={invoice.agency_name || 'No agency'}>
                          {(invoice.agency_name || 'No agency').length > 15 ? `${(invoice.agency_name || 'No agency').substring(0, 15)}...` : (invoice.agency_name || 'No agency')}
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-gray-500">Travel Date:</div>
                        <div className="font-medium break-words">
                          {new Date(invoice.travel_date).toLocaleDateString('en-GB')}
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-gray-500">Total Amount:</div>
                        <div className="font-medium break-words">
                          {(invoice as any).itinerary_currency} {invoice.total_amount?.toLocaleString() || '0'}
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-gray-500">Balance:</div>
                        <div className="font-medium break-words">
                          {(invoice as any).itinerary_currency} {invoice.balance_amount?.toLocaleString() || '0'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center flex-wrap gap-1">
                      <div className="flex items-center gap-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.payment_status)}`}>
                          {invoice.payment_status}
                        </span>
                        {invoice.invoice_needs_review && (
                          <span className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 text-red-700">
                            ⚠ Review
                          </span>
                        )}
                      </div>
                      {activeView === 'outstanding' && (
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full flex-shrink-0 ${getOverdueColor(invoice.days_overdue || 0)}`}>
                          {invoice.days_overdue || 0} days
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Desktop Table View (hidden on mobile) */}
            <div className="hidden sm:block h-full">
              <div className="overflow-x-auto h-full">
                <table className="w-full min-w-[800px] h-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button onClick={() => handleSort('itinerary_id')} className="hover:text-blue-600 flex items-center gap-1">
                          Itinerary {sortField === 'itinerary_id' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </button>
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quotation
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button onClick={() => handleSort('lead_guest_name')} className="hover:text-blue-600 flex items-center gap-1">
                          Guest / Agency {sortField === 'lead_guest_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </button>
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button onClick={() => handleSort('travel_date')} className="hover:text-blue-600 flex items-center gap-1">
                          Travel Date {sortField === 'travel_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </button>
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button onClick={() => handleSort('total_amount')} className="hover:text-blue-600 flex items-center gap-1">
                          Invoice {sortField === 'total_amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </button>
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button onClick={() => handleSort('amount_paid')} className="hover:text-blue-600 flex items-center gap-1">
                          Paid {sortField === 'amount_paid' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </button>
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button onClick={() => handleSort('balance_amount')} className="hover:text-blue-600 flex items-center gap-1">
                          Balance {sortField === 'balance_amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </button>
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button onClick={() => handleSort('payment_status')} className="hover:text-blue-600 flex items-center gap-1">
                          Status {sortField === 'payment_status' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </button>
                      </th>
                      {activeView === 'outstanding' && (
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <button onClick={() => handleSort('days_overdue')} className="hover:text-blue-600 flex items-center gap-1">
                            Overdue {sortField === 'days_overdue' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </button>
                        </th>
                      )}
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedInvoices.map((invoice) => (
                      <tr 
                        key={invoice.booking_id}
                        className={`hover:bg-gray-50 ${(invoice as any).itinerary_currency === 'USD' ? 'bg-yellow-50' : ''}`}
                      >
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {invoice.itinerary_id}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm">
                          {invoice.quotation_number ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-800 text-xs font-mono font-bold rounded-lg cursor-pointer hover:bg-blue-200"
                              onClick={() => { const id = parseInt(invoice.itinerary_id.replace('WH_', '')); setVersionsItineraryId(id); }}
                              title="View all versions"
                            >
                              <GitBranch className="w-3 h-3" />
                              {invoice.quotation_number}/{invoice.version_number}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900">
                          <div className="max-w-48">
                            <div className="font-medium truncate" title={invoice.lead_guest_name}>
                              {invoice.lead_guest_name.length > 25 ? invoice.lead_guest_name.substring(0, 25) + '...' : invoice.lead_guest_name}
                            </div>
                            <div className="text-xs text-gray-500 truncate" title={invoice.agency_name}>
                              {invoice.agency_name.length > 25 ? invoice.agency_name.substring(0, 25) + '...' : invoice.agency_name}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(invoice.travel_date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {(invoice as any).itinerary_currency} {invoice.total_amount?.toLocaleString() || '0'}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>{(invoice as any).itinerary_currency} {invoice.amount_paid?.toLocaleString() || '0'}</div>
                          {invoice.quotation_number && invoice.total_paid_all_versions != null && invoice.total_paid_all_versions !== invoice.amount_paid && (
                            <div className="text-xs text-amber-600 mt-0.5" title="Total paid across all versions under this quotation">
                              All versions: {(invoice as any).itinerary_currency} {invoice.total_paid_all_versions?.toLocaleString()}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {(invoice as any).itinerary_currency} {invoice.balance_amount?.toLocaleString() || '0'}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.payment_status)}`}>
                              {invoice.payment_status}
                            </span>
                            {invoice.invoice_needs_review && (
                              <span className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 text-red-700" title="Invoice was generated for a different version. Review needed.">
                                ⚠ Review
                              </span>
                            )}
                          </div>
                        </td>
                        {activeView === 'outstanding' && (
                          <td className="px-3 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getOverdueColor(invoice.days_overdue || 0)}`}>
                              {invoice.days_overdue || 0} days
                            </span>
                          </td>
                        )}
                        <td className="px-3 py-4 whitespace-nowrap text-sm">
                          {canManageInvoices && (
                            <button
                              onClick={(e) => handleGearClick(invoice, e)}
                              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                              title="Manage Invoice"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {sortedInvoices.length === 0 && (
              <div className="text-center py-12">
                <div className="w-12 h-12 text-gray-400 mx-auto mb-4">💰</div>
                <p className="text-gray-500">No invoices found.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal */}
        {showModal && selectedInvoice && (
          <InvoiceRevampModal
            invoice={selectedInvoice}
            onClose={() => {
              setShowModal(false);
              setSelectedInvoice(null);
              fetchInvoices(); // Refresh data
            }}
          />
        )}

        {/* Versions Modal */}
        {versionsItineraryId && (
          <VersionsModal
            itineraryId={versionsItineraryId}
            onClose={() => setVersionsItineraryId(null)}
          />
        )}
      </div>
    </div>
  );
};

export default InvoiceRevampPage;